import { useRouter } from 'next/router';

import { Alert, Box, Divider, Heading, Paragraph } from '@zillow/constellation-10';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import accessUtils from '../../../../utils/accessUtils';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
  wizardParagraphStyles,
} from '../../../../styles/wizard';

import AmenitiesInteriorFormField from '../../../PostingPath/AmenitiesInteriorFormField';
import AmenitiesWithAdditionalAmenitiesFields from '../../../PostingPath/AmenitiesWithAdditionalAmenitiesFields';
import PropertyDescriptionFormField from '../../../PostingPath/PropertyDescriptionFormField';
import PageWizard from '../../../Wizard/PageWizard';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const DescriptionAndAmenities = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const shouldShowBrokerFeeAlert = accessUtils.shouldShowBrokerFeeAlert({
    state: listingDetails?.state ?? '',
  });

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep || undefined}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <div data-testid="DescriptionAndAmenities">
        <Heading textStyle="heading-md" level={2} css={wizardHeadingStyles}>
          Description & amenities
        </Heading>
        <Paragraph css={wizardParagraphStyles}>
          Write several sentences describing upgrades, attractive amenities and other appealing details like the
          property’s proximity to transit, dining, shopping, and other local attractions. Then select all relevant
          included amenities.
        </Paragraph>
        <Box css={wizardFlexContainerStyles}>
          <Box data-testid="FormSectionContainer" css={wizardFormSectionContainerStyles.raw()}>
            <Heading level={2} textStyle="heading-xs" css={wizardHeadingStyles}>
              Property description
            </Heading>
            {shouldShowBrokerFeeAlert && (
              <Alert tone="info" css={{ marginBottom: '600' }}>
                <strong>MA Broker Fee Rule:</strong> Starting Aug 1, broker fees can be charged only to tenants who
                directly hire a broker
              </Alert>
            )}
            <PropertyDescriptionFormField showLabel={false} />
            <Divider css={{ marginBlock: '600' }} />
            <Heading textStyle="heading-sm" level={2} css={wizardHeadingStyles}>
              Interior amenities
            </Heading>
            <AmenitiesInteriorFormField />
            <Divider css={{ marginBlock: '600' }} />
            <Heading textStyle="heading-sm" level={2} css={wizardHeadingStyles}>
              Property amenities
            </Heading>
            <AmenitiesWithAdditionalAmenitiesFields />
          </Box>
        </Box>
      </div>
    </PageWizard>
  );
};

export default DescriptionAndAmenities;
